import React, { useState, useEffect } from 'react';
import { RouletteElement as RouletteElementType, QuizConfig } from '../types';

interface RouletteElementProps {
    element: RouletteElementType;
    config: QuizConfig;
    onWin?: (prize: string) => void;
    previewMode?: boolean;
}

const RouletteElement: React.FC<RouletteElementProps> = ({ element, config, onWin, previewMode }) => {
    const [spinning, setSpinning] = useState(false);
    const [rotation, setRotation] = useState(0);
    const [winner, setWinner] = useState<string | null>(null);
    const [winningIndex, setWinningIndex] = useState<number | null>(null);
    const [hasSpun, setHasSpun] = useState(false);
    const [spinDuration, setSpinDuration] = useState(4000);

    const items = element.items || [];
    const totalItems = items.length;
    const arc = 360 / (totalItems || 1);
    const radius = 145; // Adjusted radius
    const center = 150;

    // Colors for the wheel segments
    const palette = element.colorScheme === 'colorful'
        ? ['#E8E0D5', '#D6E4D9', '#E6D6E4', '#D5DFE8', '#E8D5D5', '#DFE8D5', '#F2E6D9', '#E6E6FA']
        : ['#f1f5f9', '#cbd5e1']; // Grayscale: Alternating light/darker gray

    // Removed auto-reset useEffect to prevent state loss on parent re-renders.
    // State will persist until component is unmounted or manually reset.

    const spin = () => {
        if (spinning || hasSpun) return;

        setSpinning(true);

        // Random spin duration between 3 and 5 seconds
        const duration = 3000 + Math.random() * 2000;
        setSpinDuration(duration);

        // Random rotations (at least 5 full spins)
        const spins = 5 * 360;
        // Random stop angle
        const stopAngle = Math.floor(Math.random() * 360);

        const targetRotation = spins + stopAngle;
        const newRotation = rotation + targetRotation;

        setRotation(newRotation);

        setTimeout(() => {
            setSpinning(false);
            setHasSpun(true);

            // Calculate winner based on the final rotation
            const normalizedRotation = newRotation % 360;

            // The angle of the wheel at the pointer (270deg)
            // If we rotate by R, the item at angle A moves to A + R.
            // We want A + R = 270 (mod 360) => A = 270 - R
            let winningAngle = (270 - normalizedRotation) % 360;
            if (winningAngle < 0) winningAngle += 360;

            const winningIdx = Math.floor(winningAngle / arc);
            const winningItem = items[winningIdx];

            console.log('Spin finished:', { newRotation, normalizedRotation, winningAngle, winningIdx, winningItem });

            if (winningItem) {
                setWinner(winningItem.label);
                setWinningIndex(winningIdx);
                if (onWin) onWin(winningItem.label);
            }

        }, duration + 50); // Add small buffer to ensure CSS animation finishes
    };

    // Helper to calculate SVG path for a segment
    const getSectorPath = (index: number) => {
        const startAngle = index * arc;
        const endAngle = (index + 1) * arc;

        const start = polarToCartesian(center, center, radius, endAngle);
        const end = polarToCartesian(center, center, radius, startAngle);

        const largeArcFlag = endAngle - startAngle <= 180 ? "0" : "1";

        return [
            "M", center, center,
            "L", start.x, start.y,
            "A", radius, radius, 0, largeArcFlag, 0, end.x, end.y,
            "L", center, center
        ].join(" ");
    };

    const polarToCartesian = (centerX: number, centerY: number, radius: number, angleInDegrees: number) => {
        const angleInRadians = (angleInDegrees) * Math.PI / 180.0;
        return {
            x: centerX + (radius * Math.cos(angleInRadians)),
            y: centerY + (radius * Math.sin(angleInRadians))
        };
    };

    return (
        <div className="flex flex-col items-center justify-center w-full" style={{ marginBottom: '1.5rem' }}>
            <h2 className="text-xl font-bold mb-8 text-center" style={{ color: config.colors.title }}>{element.title}</h2>

            <div className="relative w-[300px] h-[300px] mb-6">
                {/* Pointer */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-5 z-10 drop-shadow-xl">
                    <svg width="40" height="48" viewBox="0 0 50 60" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path
                            d="M25 60C25 60 5 35.5 5 20.5C5 9.4543 13.9543 0.5 25 0.5C36.0457 0.5 45 9.4543 45 20.5C45 35.5 25 60 25 60Z"
                            fill={config.colors.primary}
                            stroke="white"
                            strokeWidth="4"
                        />
                        <circle cx="25" cy="20" r="8" fill="white" fillOpacity="0.2" />
                    </svg>
                </div>

                {/* Wheel */}
                <div className="w-full h-full">
                    <svg viewBox="0 0 300 300" className="w-full h-full drop-shadow-xl rounded-full">
                        {/* Static Outer Border Circle */}
                        <circle cx={center} cy={center} r={145} fill="#ffffff" />
                        <circle cx={center} cy={center} r={145} fill="none" stroke="white" strokeWidth="10" />

                        {/* Rotating Group */}
                        <g
                            style={{
                                transform: `rotate(${rotation}deg)`,
                                transformOrigin: '150px 150px',
                                transition: spinning ? `transform ${spinDuration}ms cubic-bezier(0.2, 0, 0, 1)` : 'none'
                            }}
                        >
                            {items.map((item, index) => {
                                const angle = index * arc + arc / 2;
                                const textPos = polarToCartesian(center, center, radius * 0.58, angle);
                                const hasLabel = !!item.label && item.label.trim().length > 0;

                                return (
                                    <g key={item.id}>
                                        <path
                                            d={getSectorPath(index)}
                                            fill={palette[index % palette.length]}
                                            stroke="white"
                                            strokeWidth="8"
                                        />
                                        <g
                                            transform={`translate(${textPos.x}, ${textPos.y}) rotate(${angle + 90}) scale(${winner && winningIndex === index ? 1.5 : 1})`}
                                            style={{ transition: 'transform 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275)' }}
                                        >
                                            {hasLabel ? (
                                                <>
                                                    <text
                                                        x="0"
                                                        y="-10"
                                                        textAnchor="middle"
                                                        fontSize="28"
                                                        fill="#334155"
                                                    >
                                                        {item.emoji}
                                                    </text>
                                                    <text
                                                        x="0"
                                                        y="15"
                                                        textAnchor="middle"
                                                        fontSize="14"
                                                        fontWeight="bold"
                                                        fill="#1e293b"
                                                    >
                                                        {item.label}
                                                    </text>
                                                </>
                                            ) : (
                                                <text
                                                    x="0"
                                                    y="10"
                                                    textAnchor="middle"
                                                    fontSize="40"
                                                    fill="#334155"
                                                >
                                                    {item.emoji}
                                                </text>
                                            )}
                                        </g>
                                    </g>
                                );
                            })}
                        </g>

                        {/* Central Pin */}
                        <circle cx={center} cy={center} r={25} fill="white" stroke="#e2e8f0" strokeWidth="4" />
                    </svg>
                </div>
            </div>

            {!hasSpun ? (
                <button
                    onClick={spin}
                    disabled={spinning}
                    className="px-8 py-3 rounded-full font-bold text-white shadow-lg transform transition hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed active:scale-95"
                    style={{ backgroundColor: config.colors.primary }}
                >
                    {element.buttonText}
                </button>
            ) : (
                <div className="text-center animate-bounce bg-white p-4 rounded-xl shadow-lg border border-slate-100">
                    <h3 className="text-lg font-bold text-green-600 mb-1">
                        {(element.winningMessage || 'Você ganhou {prize}!').replace('{prize}', winner || items[winningIndex!]?.emoji || '')}
                    </h3>
                    {element.subMessage && (
                        <p className="text-sm text-slate-600">{element.subMessage}</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default RouletteElement;
